import { Box, Typography } from '@mui/material'
import React from 'react'
import { ActionButton } from '../../../components'
import { ArrowForward, QuestionAnswer } from '@mui/icons-material'
import { useTrackHomeEnterpriseEvent } from '../../home-enterprise/hooks/useTrackHomeEnterpriseEvent'
import { HOME_SECTION_TYPE } from '../../home-enterprise/components/sections/home-sections'
import { useSelector } from 'react-redux'
import { userSlice } from '../../../reducers'
import { useFormFactor, useGroup } from '../../../hooks'
import { useLPQContext } from '../../../components/lpq-provider'
import { ALL_ENTERPRISE } from '../../../services/cognito/userGroups'
import ProfileSectionContainer from './profile-section-container'
import { ProfileCard } from './profile-card'

// The home pages keep the existing wording; /profile follows the sentence-case
// convention the redesigned Account Settings uses throughout.
const HOME_SECTION_TITLE = 'Learning Experience'
const PROFILE_SECTION_TITLE = 'Learning experience'

/*
 * The promo itself, with no section chrome around it. The home pages and
 * /profile frame it differently, so the frame is the caller's choice.
 */
const LpqPromo = ({ isProfileLoaded: externalIsProfileLoaded }) => {
  const { trackClickEvent } = useTrackHomeEnterpriseEvent()
  const internalIsProfileLoaded = useSelector(userSlice.selectors.selectIsProfileLoaded)
  const profile = useSelector(userSlice.selectors.selectProfile)
  const { isMobile } = useFormFactor()
  const { openLPQ } = useLPQContext()
  const { isInGroup } = useGroup()

  const isProfileLoaded = externalIsProfileLoaded !== undefined ? externalIsProfileLoaded : internalIsProfileLoaded
  const lpqCompleted = Boolean(profile?.lpqCompleted)

  // Conditional content based on LPQ completion status
  const descriptionText = lpqCompleted
    ? 'Your preferences are shaping the recommendations you see. You can update them anytime to keep content aligned with your goals.'
    : 'Answer a few quick questions in the Learning & Progress Questionnaire to get tailored content and recommendations. It only takes a minute!'

  const buttonLabel = lpqCompleted ? 'Update my preferences' : 'Get Started'

  const handleOnboardingClick = () => {
    if (isInGroup(ALL_ENTERPRISE)) {
      trackClickEvent(HOME_SECTION_TYPE.ONBOARDING, {
        action: 'start'
      })
    }
    openLPQ()
  }

  return (
    <Box
      sx={{
        background: '#FEFCFF',
        border: '1px solid #DDDBE0',
        p: { xs: 2, sm: 3, md: 4 },
        borderRadius: '4px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: { sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 2, md: 3 },
        width: '100%',
        whiteSpace: 'normal'
      }}
      data-test='home-onboarding-rails'
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          display='flex'
          alignItems='center'
          gap={1}
          mb={{ xs: 1, sm: 1.5 }}
          flexWrap='wrap'
        >
          <QuestionAnswer
            color='primary'
            sx={{ fontSize: { xs: '20px', sm: '24px' }, flexShrink: 0 }}
          />
          <Typography
            sx={({ typography }) => ({
              fontWeight: 700,
              ...typography.body.l,
              fontSize: { xs: '16px', sm: '18px', md: typography.body.l.fontSize },
              wordBreak: 'break-word'
            })}
          >
            Help us personalize your experience
          </Typography>
        </Box>
        <Typography
          sx={{
            width: { xs: '100%', sm: '90%', md: '80%' },
            color: '#18191A',
            fontSize: { xs: '14px', sm: '15px', md: '16px' },
            lineHeight: { xs: 1.5, sm: 1.6 },
            whiteSpace: 'normal',
            wordBreak: 'break-word'
          }}
        >
          {descriptionText}
        </Typography>
      </Box>
      <Box
        sx={{
          width: { xs: '100%', sm: 'auto' },
          textAlign: 'center',
          display: 'flex',
          justifyContent: { xs: 'stretch', sm: 'center' },
          flexShrink: 0
        }}
      >
        <ActionButton
          variant='primary'
          label={buttonLabel}
          onClick={handleOnboardingClick}
          loading={!isProfileLoaded}
          fullWidth={isMobile}
          endIcon={<ArrowForward />}
          data-test='onboarding-section-start-button'
          sx={{ minWidth: { sm: '200px' } }}
        />
      </Box>
    </Box>
  )
}

/**
 * Home-page framing (paid consumer and enterprise). Unchanged.
 */
export const ProfileLpqSection = ({ isProfileLoaded }) => (
  <ProfileSectionContainer
    id='profile-learning-experience'
    sectionTitle={HOME_SECTION_TITLE}
  >
    <LpqPromo isProfileLoaded={isProfileLoaded} />
  </ProfileSectionContainer>
)

/**
 * /profile framing: the outlined card every other Account Settings section uses.
 */
export const ProfileLpqCard = ({ isProfileLoaded }) => (
  <ProfileCard
    id='profile-learning-experience'
    dataTest='profile-learning-experience'
    title={PROFILE_SECTION_TITLE}
  >
    <LpqPromo isProfileLoaded={isProfileLoaded} />
  </ProfileCard>
)
